import { FoodHeader } from '@cf-shared/data-model/food';
import { Ingredient } from '@cf-shared/data-model/ingredient';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { deepClone } from '@cf-shared/deep-helpers';

/**
 * How the meal screen's recent meals are drawn while the meal is still empty
 * (mh-physique#4969).
 *
 * `hero` is the meal the user most likely came here to repeat: their own last meal in THIS
 * slot -- the last "Meal 1" when they are standing in Meal 1. It is drawn as a card of its
 * own, showing the foods it holds and its macros, with one button that logs the whole thing.
 * `others` are drawn under it as plain rows, exactly as all three were drawn before.
 *
 * `hero` is UNDEFINED whenever no recent meal matches, which is an ordinary case rather than
 * a fault: a first-ever meal, or a meal slot the user has never used, has no last time to
 * repeat. The block then draws the plain rows on their own.
 */
export interface RecentMealsLayout {
  hero: FoodHeader | undefined;
  others: FoodHeader[];
}

/**
 * Picks the meal to offer up front, and leaves the rest in the order they arrived.
 *
 * The match is made HERE rather than trusted from the caller's ordering. The loader that
 * fetches these meals does sort a matching meal to the front today, but that is its own
 * decision about ordering, and a layout that silently depended on it would draw the wrong
 * meal as "your last Meal 1" the moment that ordering changed for any other reason.
 *
 * Every input is treated as untrustworthy, because these come from stored documents that
 * older versions of the app wrote: a missing list, a null entry, an entry whose meal was
 * not cached, or a meal with no name are all dropped or ignored instead of throwing.
 */
export function splitRecentMeals(
  recentMeals: FoodHeader[] | undefined,
  currentMealName: string | undefined,
): RecentMealsLayout {
  const usable = Array.isArray(recentMeals) ? recentMeals.filter(meal => !!meal && !!meal._id) : [];
  if (!currentMealName) {
    return { hero: undefined, others: usable };
  }

  const heroIndex = usable.findIndex(meal => (meal.cachedFood as Log_Meal | undefined)?.name === currentMealName);
  if (heroIndex < 0) {
    return { hero: undefined, others: usable };
  }

  return {
    hero: usable[heroIndex],
    others: usable.filter((_meal, index) => index !== heroIndex),
  };
}

/**
 * The ingredients to put in the open meal when the user repeats a recent one.
 *
 * They are always CLONED, for the same reason "Copy From a Day" clones (see
 * `mergeCopiedMealIngredients` in `pages/edit-meal/copy-meal-ingredients.ts`): the database
 * hands back one shared object per document, and the ingredient list edits its array in
 * place. Without the clone, every later edit to today's meal would silently rewrite the meal
 * the user logged on the earlier day.
 *
 * Entries that are not usable ingredients are dropped rather than copied through. A stored
 * meal written by an older version of the app can hold a null entry or one with no weight,
 * and passing that on would put a card in the meal that nothing can draw or total up.
 */
export function ingredientsToRepeat(logMeal: Log_Meal | undefined): Ingredient[] {
  const stored = logMeal?.ingredients;
  if (!Array.isArray(stored)) return [];

  // `Number.isFinite`, not `typeof === 'number'`: NaN and the infinities are all numbers to
  // `typeof`, and a NaN weight would be pushed into the meal, added into the running total and
  // saved — turning today's stored meal into one nothing can draw or total up. That the shape
  // occurs at all is why `updateNutrientsAsync` in ingredient-list.ts already skips NaN weights.
  const usable = stored.filter(ingredient => !!ingredient && Number.isFinite(ingredient.weight_g));
  return deepClone(usable);
}
